import * as React from 'react'
import classNames from 'classnames'

import { Text, TextName } from 'sdk'
import { GradientContainer } from 'pages/soundscape-page/components'
import { OutlineGlassBackgroundCard } from 'components/outline-glass-background-card/OutlineGlassBackgroundCard'
import motherboardIcon from 'shared/assets/icons/motherboard.svg'

import { useWindowSize } from 'core/hooks/useWindowSize'
import type { SoundscapeOnePlatformCmsContent } from 'core/services/cms/interfaces'

import stepFilledBg from './assets/step-filled-bg.jpeg'

import classes from './style.module.scss'

interface OnePlatformProps {
  content: SoundscapeOnePlatformCmsContent
}

const renderMultilineText = (text: string): React.ReactNode => {
  const lines = text.split('\n')

  return lines.flatMap((line, index) => (
    index === 0 ? [line] : [<br key={`break-${index}`} />, line]
  ))
}

export const OnePlatform: React.FC<OnePlatformProps> = ({ content }) => {
  const { width } = useWindowSize()
  const isBelowLg = width > 0 && width <= 1024

  return (
    <section className={classes.content}>
      <Text type={TextName.H2} className={classes.title}>
        {renderMultilineText(content.title)}
      </Text>

      <div className={classes.steps}>
        {content.steps.map((step, index) => {
          const isHighlighted = index === content.steps.length - 1
          const stepStyle = isHighlighted ? {
            backgroundImage: `url(${stepFilledBg})`,
            backgroundSize: 'cover',
          } : {}

          return isBelowLg ? (
            <OutlineGlassBackgroundCard
              key={index}
              className={classNames(classes.mobileStep, { [classes.gradient]: isHighlighted })}
            >
              <Text className={classes.badge} type={TextName.Subtext}>
                Step {index + 1}
              </Text>

              <Text type={TextName.TextBold}>{step.title}</Text>
            </OutlineGlassBackgroundCard>
          ) : (
            <GradientContainer
              key={index}
              style={stepStyle}
              className={classNames(classes.step, { [classes.gradient]: isHighlighted })}
            >
              <img className={classes.image} src={step.imageUrl} alt='' />

              <Text className={classes.badge} type={TextName.Subtext}>
                Step {index + 1}
              </Text>

              <Text type={TextName.TextBold}>{step.title}</Text>
            </GradientContainer>
          )
        })}
      </div>

      <OutlineGlassBackgroundCard className={classes.hardwareCard}>
        <img src={motherboardIcon} alt='motherboard' width={75} height={75} />

        <div className={classes.hardwareContent}>
          <Text type={TextName.TextBold}>{content.hardwareSupportTitle}</Text>

          <ul className={classes.hardwareList}>
            {content.hardwareSupportItems.map((item) => (
              <li key={item}>
                <Text type={TextName.Text}>{item}</Text>
              </li>
            ))}
          </ul>
        </div>
      </OutlineGlassBackgroundCard>
    </section>
  )
}
